import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiStepForm, Step } from '../../../../components/molecules/Form/components/MultiStepForm';
import { Namespace } from '../../../../translation/namespace';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import { CreatePageChoice } from './partials/CreatePageChoice';
import { PageInformation, PageInformationParamType } from './partials/PageInformation';
import { IParameterProperty } from '../../../../types/IParameter';
import { TeamRoomAdmissionTypeOptions, TeamroomFormTemplate } from './partials/TeamroomFormTemplate';
import { EventFormTemplate } from './partials/EventFormTemplate';
import { ExternalLinkFormTemplate } from './partials/ExternalLinkFormTemplate';
import { TeaserInformationFormTemplate } from './partials/TeaserInformationFormTemplate';
import { ExternalContentFormTemplate } from './partials/ExternalContentFormTemplate';
import { PageType } from '../../../../types/api/PageType';
import { FetchState } from '../../../../hooks/reducers/useFetchReducer';
import {
  HiddenParam,
  RadioParam,
  SelectParam,
  SubmitButton,
  TextAreaParam,
  TextParam,
  UrlParam,
} from '../../../../_lib/Form2';
import { useFetch } from '../../../../hooks/useFetch';
import { formParamsUtils } from '../../../../_lib/Form2/utils/formParamsUtils';
import { AxiosError, AxiosResponse } from 'axios';
import { SonResponseError } from '../../../../_lib/Form2/types/SonResponseError';
import { NestedParam } from '../../../../_lib/Form2/types/NestedParam';
import { ImageFileParam } from '../../../../_lib/Form2/types/FileUploadParam';
import { DateRangeParam } from '../../../../_lib/Form2/types/DateRangeParam';
import { FieldSet } from '../../../../_lib/Form2/types/FieldSet';
import usePrevious from '../../../../hooks/usePrevious';
import { TemplateChoice } from './partials/TemplateChoice';

const TAB_PANEL_IDS = {
  PAGE_TYPE: 'multi-step-form-page-type',
  TEMPLATES: 'multi-step-form-templates',
  PAGE_INFORMATIONS: 'multi-step-form-page-informations',
  PAGE_PROPERTIES: 'multi-step-form-page-properties',
};

type CreatPageFormTemplateParamTypes = TextAreaParam<'teaserText'>
  | UrlParam<'url'>
  | UrlParam<'conferenceRoomUrl'>
  | TextParam<'name'>
  | TextAreaParam<'additionalInfo'>
  | TextParam<'location'>
  | HiddenParam<'teamroomOnlyRequested'>
  | SelectParam<'teamroomAdmissionType', TeamRoomAdmissionTypeOptions>
  | SelectParam<'initialReadRestriction'>
  | DateRangeParam<'daterange'>
  | FieldSet<'pageTypes', RadioParam<'pageType'>>
  | NestedParam<'imageuploadparams', 'imageupload',
    TextParam<'alt'> | TextParam<'copyright'> | ImageFileParam<'image'>>;

type CreatePageFormTemplateProps = {
  params: ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>;
  fetchState: FetchState<AxiosResponse<any>, AxiosError<SonResponseError>>;
};

const shouldHaveSubmitButton = (tabName: string, additionalTabs: string[] | undefined) => {
  if (additionalTabs && additionalTabs.length) {
    return additionalTabs?.findIndex(value => value === tabName) === additionalTabs?.length - 1;
  }

  return false;
};

const getTabFormTemplate = (
  params: ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>,
  selectedPageType: PageType | undefined) => {
  const url = params.findByName('url');
  const name = params.findByName('name');
  const teamroomAdmissionType = params.findByName('teamroomAdmissionType');
  const teamroomOnlyRequested = params.findByName('teamroomOnlyRequested');
  const imageUploadParams = params.findByName('imageuploadparams');
  const teaserText = params.findByName('teaserText');
  const additionalInfo = params.findByName('additionalInfo');
  const conferenceRoomUrl = params.findByName('conferenceRoomUrl');
  const location = params.findByName('location');
  const daterange = params.findByName('daterange');

  // External Application
  if (selectedPageType === PageType.externalapplication && url) {
    return <ExternalContentFormTemplate urlParam={url} />;
  }
  // Teamroom
  if (teamroomAdmissionType) {
    return (
      <TeamroomFormTemplate
        teamroomAdmissionTypeParam={teamroomAdmissionType}
        teamroomOnlyRequestedParam={teamroomOnlyRequested}
      />);
  }
  // External Link (Subpage of pagelist)
  if (url && name) {
    return <ExternalLinkFormTemplate urlParam={url} nameParam={name} />;
  }
  // Threads (Subpage of forum)
  if (imageUploadParams && teaserText) {
    return <TeaserInformationFormTemplate imageUploadParams={imageUploadParams} teaserTextParam={teaserText} />;
  }
  // Events (Subpage of eventlist)
  return (
    <EventFormTemplate
      dateRangeParam={daterange}
      additionalInfoParam={additionalInfo}
      locationParam={location}
      conferenceRoomUrlParam={conferenceRoomUrl}
    />
  );
};

export function CreatePageFormTemplate({
  params,
  fetchState,
}: CreatePageFormTemplateProps) {
  const { t } = useTranslation(Namespace.CreatePage);
  const { portalConfiguration } = useAppContext();
  const { get, requestError, isLoading } = useFetch();

  const [selectedPageType, setSelectedPageType] = useState<PageType | undefined>(undefined);
  const [formParamsFetchUrl, setFormParamsFetchUrl] = useState<string | undefined>(undefined);
  const [additionalTabs, setAdditionalTabs] = useState<string[] | undefined>(undefined);
  const previousFormParamsFetchUrl = usePrevious(formParamsFetchUrl);
  const [fetchedFormParameters, setFetchedFormParameters] = useState(
    formParamsUtils({ params: [] } as any));

  const fetchAdditionalFormParameters = useCallback(async () => {
    if (formParamsFetchUrl && formParamsFetchUrl !== previousFormParamsFetchUrl
    ) {
      const res = await get<Record<string, IParameterProperty[]>>(formParamsFetchUrl);

      if (res) {
        setFetchedFormParameters(formParamsUtils({
          params: Object.values(res.data.tabs).reduce((acc, curr) => {
            return acc.concat(curr);
          }, [] as any[]),
        }));
      }
    }
  }, [get, formParamsFetchUrl, previousFormParamsFetchUrl]);

  const getSubmitButton = useMemo(() => {
    if (selectedPageType) {
      return (
        <SubmitButton
          buttonLabel={t(`${Namespace.PageManagement}:views.create-page-general.button`)}
          messageSuccess={
            t(
              `${Namespace.PageManagement}:views.create-page-general.status.success`,
              { page: selectedPageType },
            )
          }
          messageForward={`${Namespace.PageManagement}:views.create-page-general.status.successForward`}
          messageSending={t(
            `${Namespace.PageManagement}:views.create-page-general.status.sending`,
            { page: selectedPageType },
          )}
          messageFail={
            <>
              {t(
                `${Namespace.PageManagement}:views.create-page-general.status.fail`,
                { page: selectedPageType },
              )}
              {translatePartOfStringWithLink(
                `${Namespace.phrases}:ifProblemCallLinkedSupport`,
                portalConfiguration.son_portal_support_email
                  ? `mailto:${portalConfiguration.son_portal_support_email}`
                  : null,
              )}
            </>
          }
          fetchState={fetchState}
        />
      );
    }

    return null;
  }, [fetchState, portalConfiguration.son_portal_support_email, selectedPageType, t]);

  return (
    <MultiStepForm
      initialActiveTab={TAB_PANEL_IDS.PAGE_TYPE}
      fetchingData={{
        fetchFormParams: fetchAdditionalFormParameters,
        isLoading,
        requestError,
        loadingMessage: t(`${Namespace.phrases}:contentIsLoading`),
      }}
    >
      <Step id={TAB_PANEL_IDS.PAGE_TYPE} buttonLabel={t('steps.pageType.title')}>
        <CreatePageChoice
          params={(params.getByName('pageTypes') as FieldSet<'pageTypes', RadioParam<'pageType'>>)?.params}
          setSelectedPageTypeParameters={(param) => {
            setSelectedPageType(param?.value as PageType);
            setFormParamsFetchUrl(param?.formParamsFetchUrl);
            setAdditionalTabs(param?.additionalTabs);
          }}
        />
      </Step>
      {additionalTabs?.includes('templates')
        ? <Step
            id={TAB_PANEL_IDS.TEMPLATES}
            orderKey={additionalTabs.findIndex(el => el === 'templates')}
            buttonLabel={t('steps.templates.title')}
          >
            <TemplateChoice
              params={fetchedFormParameters.getAllByName('templatePageId') as RadioParam<'templatePageId'>[]}
              selectedPageTitle={t(`${Namespace.PageType}:${selectedPageType}.title`)}
              setSelectedFetchUrl={setFormParamsFetchUrl}
            />
          </Step>
        : null}
      <Step
        id={TAB_PANEL_IDS.PAGE_INFORMATIONS}
        orderKey={additionalTabs?.findIndex(el => el === 'basicPageInformation')}
        buttonLabel={t('steps.pageInformation.title')}
        submitButton={shouldHaveSubmitButton('basicPageInformation', additionalTabs)
          ? getSubmitButton
          : null}
        disabled={!additionalTabs || additionalTabs.length === 0}
      >
        <PageInformation
          pageType={selectedPageType as PageType}
          params={fetchedFormParameters as PageInformationParamType}
        />
      </Step>
      {additionalTabs?.includes('additionalPageInformation') && (
        <Step
          id={TAB_PANEL_IDS.PAGE_PROPERTIES}
          orderKey={additionalTabs.findIndex(el => el === 'additionalPageInformation')}
          buttonLabel={t('steps.pageProperties.title')}
          submitButton={getSubmitButton}
          disabled={!additionalTabs || additionalTabs.length === 0}
        >
          {getTabFormTemplate(
            fetchedFormParameters as ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>,
            selectedPageType)}
        </Step>
      )}
    </MultiStepForm>
  );
}
